import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

/**
 * Hook to handle automatic language detection and provide feedback to users
 */
export const useLanguageDetection = () => {
  const { i18n } = useTranslation();
  const [detectedLanguage, setDetectedLanguage] = useState<string | null>(null);
  const [detectionSource, setDetectionSource] = useState<string | null>(null);

  useEffect(() => {
    // Check if this is the first visit (no language preference stored)
    const storedLanguage = localStorage.getItem('i18nextLng');
    const isFirstVisit = !storedLanguage || storedLanguage === 'undefined';

    if (isFirstVisit && i18n.language) {
      // Detect the source of language detection
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      
      let source = 'browser settings';
      if (urlLang) {
        source = 'URL parameter';
      } else if (navigator.language) {
        source = 'browser settings';
      }

      setDetectedLanguage(i18n.language);
      setDetectionSource(source);

      // Show a helpful toast notification
      const languageName = i18n.language === 'pt' ? 'Português' : 'English';
      const message = i18n.language === 'pt' 
        ? `Idioma definido automaticamente para ${languageName} com base nas suas configurações`
        : `Language automatically set to ${languageName} based on your ${source}`;

      // Show toast after a short delay to avoid overwhelming the user
      setTimeout(() => {
        toast.info(message, {
          duration: 5000,
          description: i18n.language === 'pt'
            ? 'Pode alterar o idioma a qualquer momento no cabeçalho'
            : 'You can change the language anytime in the header',
        });
      }, 1000);
    }
  }, [i18n.language]);

  /**
   * Get information about how the language was detected
   */
  const getDetectionInfo = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    const storedLang = localStorage.getItem('i18nextLng');
    const browserLang = navigator.language;
    const cookieLang = document.cookie
      .split('; ')
      .find(row => row.startsWith('i18next='))
      ?.split('=')[1];

    return {
      current: i18n.language,
      sources: {
        url: urlLang,
        localStorage: storedLang,
        cookie: cookieLang,
        browser: browserLang,
      },
      detectedLanguage,
      detectionSource,
    };
  };

  /**
   * Check if user's browser language matches current language
   */
  const isBrowserLanguageMatch = () => {
    const browserLang = navigator.language.split('-')[0].toLowerCase();
    return browserLang === i18n.language;
  };

  /**
   * Get suggested language based on browser settings
   */
  const getSuggestedLanguage = () => {
    const browserLang = navigator.language.split('-')[0].toLowerCase();
    const supportedLanguages = ['en', 'pt'];
    
    if (supportedLanguages.includes(browserLang)) {
      return browserLang;
    }
    
    return 'en';
  };

  return {
    detectedLanguage,
    detectionSource,
    getDetectionInfo,
    isBrowserLanguageMatch,
    getSuggestedLanguage,
  };
};
